import { BudgetPartItemsDTO } from "@shared/models/budget-part-items-dto";
import { BudgetServiceItemsDTO } from "@shared/models/budget-service-items-dto";
import {
  PartStructure,
  ServiceStructure
} from "src/app/modules/private/modules/service-request/components/components/budget/models/screen-structure";
import { Injectable } from "@angular/core";
import { MonetaryService } from "./monetary.service";
import { BudgetDTO } from "@shared/models/budget-dto";

export interface TotalsBudget {
  partItems: {
    client: number;
    carworkshop: number;
  };
  serviceItems: {
    client: number;
    carworkshop: number;
  };
  budget: {
    client: number;
    carworkshop: number;
  };
  savingPrice?: number;
}

export interface TotalsBudgetFee {
  totalParts: number;
  totalServices: number;
  totalOs: number;
  totalToPayWorkshopParts: number;
  totalToPayWorkshopServices: number;
  totalToPayWorkshop: number;
  intermediationFeePartsServices: number;
  intermediationFeeTire: number;
  totalIntermediationFee: number;
  totalAdmFee: number;
  totalDriveBMargin: number;
  percentageDriveBMargin: number;
  savingPrice?: number;
}

@Injectable({
  providedIn: "root"
})
export class BudgetStructureService {
  constructor(private monetaryService: MonetaryService) {}

  getServiceDataByColumnStructure(
    service: BudgetServiceItemsDTO,
    column: ServiceStructure
  ): string {
    let value = service[column.columnDef];

    if (column.isMonetary) {
      value = this.monetaryService.floatToMonetaryString(value);
    }

    return value;
  }

  getPartDataByColumnStructure(
    part: BudgetPartItemsDTO,
    column: PartStructure
  ): string {
    let value: any = "";
    if (Array.isArray(column.key)) {
      column.key.forEach((item) => {
        value += column.child ? part[column.child][item] : part[item];
        value += " ";
      });
    } else {
      value = column.child ? part[column.child][column.key] : part[column.key];
    }

    if (column.isMonetary) {
      value = this.monetaryService.floatToMonetaryString(value);
    }

    if (column.isFractionalNumber) {
      value = `${
        part.partAmount[0] === "," ? "0" : ""
      }${part.partAmount.toLocaleString("pt-BR")} ${
        part.measurementUnit ? part.measurementUnit : "UN"
      }`;
    }

    let newValue = "";
    //caso o valor seja muito grande
    if (value && value.length >= 13) {
      const textArr = value.split(" ");
      let maxLine = 13; //número máximo de caracteres por linha

      for (let i = 0; i < textArr.length; i++) {
        if (newValue.length + textArr[i].length <= maxLine) {
          newValue += textArr[i] + " ";
        } else {
          //quebra a linha, caso neecessário
          newValue += "\n" + textArr[i] + " ";
          maxLine = maxLine + 13;
        }
      }
    }
    value =
      newValue != ""
        ? newValue.trim().toLowerCase()
        : value
        ? value.toLowerCase()
        : "";
    value = value.charAt(0).toUpperCase() + value.slice(1);
    //retorna o valor alterado
    return value;
  }

  checkInternalBasepartToWarnUser(
    part: BudgetPartItemsDTO,
    column: PartStructure
  ) {
    return column["columnCellDef"] == "label-part-description-short" &&
      !part.isInternalReferenceBase
      ? true
      : false;
  }

  checkTireIconToShow(part: BudgetPartItemsDTO, column: PartStructure) {
    return column["columnCellDef"] === "input-code" && part.tire ? true : false;
  }

  checkCourtesyIconToShow(part: BudgetPartItemsDTO, column: PartStructure) {
    return column["columnCellDef"] === "input-code" && part.courtesy
      ? true
      : false;
  }

  checkGuaranteeIconToShow(part: BudgetPartItemsDTO, column: PartStructure) {
    return column["columnCellDef"] === "input-code" && part.guarantee
      ? true
      : false;
  }

  checkCourtesyIconToShowService(
    service: BudgetServiceItemsDTO,
    column: ServiceStructure
  ) {
    return column["columnCellDef"] === "input-code" && service.courtesy
      ? true
      : false;
  }

  checkGuaranteeIconToShowService(
    service: BudgetServiceItemsDTO,
    column: ServiceStructure
  ) {
    return column["columnCellDef"] === "input-code" && service.guarantee
      ? true
      : false;
  }

  calcTotals(
    totals: TotalsBudget,
    budgetPartItems: BudgetPartItemsDTO[],
    budgetServiceItems: BudgetServiceItemsDTO[]
  ): TotalsBudget {
    totals.serviceItems = {
      client: budgetServiceItems.reduce(
        (acc, service) => acc + service.clientPriceFinal,
        0
      ),
      carworkshop: budgetServiceItems.reduce(
        (acc, service) => acc + service.carWorkshopPrice,
        0
      )
    };

    totals.partItems = {
      client: budgetPartItems.reduce((acc, part) => {
        const totalPartClient =
          typeof part.totalPartClient == "string"
            ? this.monetaryService.monetaryStringToFloat(part.totalPartClient)
            : part.totalPartClient;
        return acc + totalPartClient * 1;
      }, 0),
      carworkshop: budgetPartItems.reduce((acc, part) => {
        const totalPartCarworkshop =
          typeof part.totalPartCarworkshop == "string"
            ? this.monetaryService.monetaryStringToFloat(
                part.totalPartCarworkshop
              )
            : part.totalPartCarworkshop;
        return acc + totalPartCarworkshop * 1;
      }, 0)
    };

    totals.budget = {
      client: totals.partItems.client + totals.serviceItems.client,
      carworkshop:
        totals.partItems.carworkshop + totals.serviceItems.carworkshop
    };

    return totals;
  }

  calcLabor(
    budgetServiceItems: BudgetServiceItemsDTO[],
    budgetPartItems: BudgetPartItemsDTO[],
    serviceName: string
  ) {
    const labor: BudgetServiceItemsDTO = {
      serviceName,
      clientPrice: budgetPartItems.reduce(
        (acc, part) => acc + part.budgetServiceItem.clientPrice,
        0
      ),
      clientPriceFinal: budgetPartItems.reduce(
        (acc, part) => acc + part.budgetServiceItem.clientPriceFinal,
        0
      ),
      carWorkshopPrice: budgetPartItems.reduce(
        (acc, part) => acc + part.budgetServiceItem.carWorkshopPrice,
        0
      ),
      amountOfHours: budgetPartItems.reduce(
        (acc, part) => acc + part.budgetServiceItem.amountOfHours,
        0
      ),
      budgetId: null,
      serviceId: 0,
      type: "LABOR",
      isPreventiveVerification: false,
      isPreventiveVerificationConfirmed: false
    };

    const existsLabor =
      budgetServiceItems.length && budgetServiceItems[0].type === "LABOR";
    const laborHasValue = labor.carWorkshopPrice > 0 || labor.clientPrice > 0;

    if (existsLabor) {
      if (laborHasValue) {
        budgetServiceItems[0] = labor;
      } else {
        budgetServiceItems.splice(0, 1);
      }
    } else if (laborHasValue) {
      budgetServiceItems = [labor, ...budgetServiceItems];
    }

    return budgetServiceItems;
  }

  calcTotalsFee(
    totalsFee: TotalsBudgetFee,
    budget: BudgetDTO,
    budgetPartItems: BudgetPartItemsDTO[],
    budgetServiceItems: BudgetServiceItemsDTO[]
  ): TotalsBudgetFee {
    totalsFee.totalParts = budgetPartItems.reduce(
      (acc, part) =>
        acc + (part.clientPrice || 0) * Number(part.partAmount || 1),
      0
    );

    totalsFee.totalServices = budgetServiceItems.reduce(
      (acc, service) => acc + (service.clientPrice || 0),
      0
    );

    totalsFee.totalOs = totalsFee.totalParts + totalsFee.totalServices;

    totalsFee.totalToPayWorkshopParts = budgetPartItems.reduce(
      (acc, part) =>
        acc + (part.carWorkshopPrice || 0) * Number(part.partAmount || 1),
      0
    );

    totalsFee.totalToPayWorkshopServices = budgetServiceItems.reduce(
      (acc, service) => acc + (service.carWorkshopPrice || 0),
      0
    );

    totalsFee.totalToPayWorkshop =
      totalsFee.totalToPayWorkshopParts + totalsFee.totalToPayWorkshopServices;

    const nonTireParts = budgetPartItems.filter((part) => !part.tire);
    const tireParts = budgetPartItems.filter((part) => part.tire);

    totalsFee.intermediationFeePartsServices =
      nonTireParts.reduce(
        (acc, part) =>
          acc + (part.intermediationFee || 0) * Number(part.partAmount || 1),
        0
      ) +
      budgetPartItems.reduce(
        (acc, part) => acc + (part.budgetServiceItem.intermediationFee || 0),
        0
      ) +
      budgetServiceItems.reduce(
        (acc, service) => acc + (service.intermediationFee || 0),
        0
      );

    totalsFee.intermediationFeeTire = tireParts.reduce(
      (acc, part) =>
        acc + (part.intermediationFee || 0) * Number(part.partAmount || 1),
      0
    );

    totalsFee.totalIntermediationFee =
      budgetPartItems.reduce(
        (acc, part) =>
          acc + (part.intermediationFee || 0) * Number(part.partAmount || 1),
        0
      ) +
      budgetPartItems.reduce(
        (acc, part) => acc + (part.budgetServiceItem.intermediationFee || 0),
        0
      ) +
      budgetServiceItems.reduce(
        (acc, service) => acc + (service.intermediationFee || 0),
        0
      );

    totalsFee.totalAdmFee =
      budgetPartItems.reduce(
        (acc, part) => acc + (part.admFee || 0) * Number(part.partAmount || 1),
        0
      ) +
      budgetPartItems.reduce(
        (acc, part) => acc + (part.budgetServiceItem.admFee || 0),
        0
      ) +
      budgetServiceItems.reduce(
        (acc, service) => acc + (service.admFee || 0),
        0
      );

    totalsFee.totalDriveBMargin =
      budgetPartItems.reduce(
        (acc, part) =>
          acc + (part.driveBMargin || 0) * Number(part.partAmount || 1),
        0
      ) +
      budgetPartItems.reduce(
        (acc, part) => acc + (part.budgetServiceItem.driveBMargin || 0),
        0
      ) +
      budgetServiceItems.reduce(
        (acc, service) => acc + (service.driveBMargin || 0),
        0
      );

    totalsFee.percentageDriveBMargin = totalsFee.totalOs
      ? (totalsFee.totalDriveBMargin / totalsFee.totalOs) * 100
      : 0;

    totalsFee.savingPrice = budget.savingPrice;

    return totalsFee;
  }
}
